import { BaseSelectorOption } from "@/types";
import { Autocomplete, ListItem, ListItemText, SxProps, createFilterOptions } from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import debounce from "lodash/debounce";
import { Component } from "react";
import { AppContext } from "../../common/AppContext";
import DropIcon from "../../resources/images/icons-chevron-double.svg";
import RemoveIcon from "../../resources/images/icons-remove.svg";
import { TextField } from "../TextField";

export interface LocationFilterProps<T extends BaseSelectorOption = BaseSelectorOption> {
  items?: T[] | null;
  value?: T | NonNullable<T> | T[] | null;
  onChange?: (
    event: React.SyntheticEvent<Element, Event>,
    newValue: T | NonNullable<T> | T[] | null
  ) => void;
  onBlur?: (event: any) => void;
  onNewItemClick?: () => void;
  initialItems?: T[] | null;
  initialSearch?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  hasNoneItem?: boolean;
  noneItemLabel?: string;
  hasAllItem?: boolean;
  allItemLabel?: string;
  getItemId?: (item: T | string) => string | number;
  getItemLabel?: (item: T) => string;
  debounce?: boolean;
  clearOnSelection?: boolean;
  clearResultsOnSelection?: boolean;
  noOptionsText?: string;
  excludeItems?: T[];
  matchFrom?: "any" | "start";
  callApi?: boolean;
  filterItems?: (items: T[]) => T[];
  listType?: string;
  getData?: (value: string) => Promise<any> | null;
  label?: string;
  sx?: SxProps;
  placeholder?: string;
  focusPlaceholder?: string;
  maxResults?: number;
  validationText?: string;
  helperText?: string;
  disabled?: boolean;
  loading?: boolean;
  compareItems?: (a: T, b: T) => boolean;
  translate?: (key: string, options?: Record<string, unknown>) => string;
}

interface LocationFilterState<T extends BaseSelectorOption = BaseSelectorOption> {
  selectedValues?: T | NonNullable<T> | T[] | null;
  allValues?: T[] | null;
  forceBlur?: boolean;
}

export class LocationFilter<T extends BaseSelectorOption = BaseSelectorOption> extends Component<
  LocationFilterProps<T>,
  LocationFilterState<T>
> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    listType: "Filter",
    maxResults: 50,
    initialSearch: false,
    multiple: true,
    clearable: true,
    hasNoneItem: false,
    hasAllItem: false,
    getItemId: null,
    debounce: true,
    clearOnSelection: false,
    clearResultsOnSelection: false,
    excludeItems: [],
    matchFrom: "any" //start or any
  };

  initialItems?: T[] | null;
  value?: T | T[] | null;
  hasTyped: boolean;
  filterOptions: any;
  debounceLoadItems: any;
  ignorePendingResults: boolean;
  dataList: any;

  constructor(props: LocationFilterProps<T>) {
    super(props);

    const allItems = this.filterItems(props.items || props.initialItems || []);

    this.state = {
      selectedValues: props.value ? this.findValue(allItems, props.value) : null,
      allValues: allItems,
      forceBlur: false
    };

    this.initialItems = props.initialItems;
    this.value = props.value;
    this.hasTyped = false;

    this.filterOptions = createFilterOptions<T>({
      matchFrom: props.matchFrom,
      // limit: props.maxResults,
      ignoreCase: true,
      stringify: (option) => this.getItemLabel(option)
    });

    this.debounceLoadItems = debounce((s) => this.loadItems(s), 500);
    this.ignorePendingResults = false;
  }

  onChange(e: React.SyntheticEvent<Element, Event>, selected: T | NonNullable<T> | T[] | null) {
    const { onChange, clearOnSelection, clearResultsOnSelection } = this.props;

    if (clearOnSelection)
      this.setState(
        {
          selectedValues: null,
          forceBlur: true,
          ...(clearResultsOnSelection && { allValues: null })
        },
        () => {
          onChange?.(e, selected);
        }
      );
    else
      this.setState({ selectedValues: selected }, () => {
        onChange?.(e, selected);
      });
  }

  getItemLabel(item: T) {
    const { getItemLabel, hasNoneItem, noneItemLabel, hasAllItem, allItemLabel } = this.props;
    if (getItemLabel) return getItemLabel(item);

    if (hasNoneItem && item.isNoneItem === true) return noneItemLabel || "";
    else if (hasAllItem && item.isAllItem === true) return allItemLabel || "";
    return item.label || "";
  }

  clearResults() {
    const { items } = this.props;
    this.ignorePendingResults = true;
    this.value = null;
    this.setItems(this.initialItems ? this.initialItems : items ? items : []);
  }
  onBlur(e: any) {
    const { onBlur } = this.props;
    const { forceBlur } = this.state;

    if (forceBlur) this.setState({ forceBlur: false });

    if (onBlur) onBlur(e);
  }
  componentDidMount() {
    if (this.props.initialSearch) this.loadItems(null, true);
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const { allValues } = this.state;

    if (prevProps.initialItems !== this.props.initialItems) {
      this.initialItems = this.props.initialItems;
      this.setItems(this.props.initialItems);
    }
    if (prevProps.items !== this.props.items) {
      this.setItems(this.props.items);
    }
    if (prevProps.value !== this.props.value) {
      this.value = this.props.value;
      this.setState({ selectedValues: this.findValue(allValues, this.value) });
    }
  }

  findValue(items: any, value: any) {
    if (value && typeof value === "object") return value;
    return items?.find((item: any) => this.getItemId(item) === value) || null;
  }

  setItems(allValues: any) {
    const filteredItems = this.filterItems(allValues);
    this.setState({
      allValues: filteredItems
    });
  }

  filterItems(allValues: any) {
    const { filterItems } = this.props;
    if (filterItems) return filterItems(allValues);
    return allValues;
  }

  loadItems(value: any, initialSearch?: any) {
    const { callApi, listType } = this.props;

    if (callApi === false && initialSearch !== true) return;

    this.value = value;
    if (initialSearch !== true && (!value || value.length === 0)) return;

    this.context.setLoading("search" + listType, true);

    this.getData(value)
      .then((list: any) => {
        if (this.ignorePendingResults) this.ignorePendingResults = false;
        else {
          this.setItems(list);
        }
      })
      .catch((error: any) => {
        //Todo: handle error
      })
      .finally(() => this.context.setLoading("search" + listType, false));
  }

  getData(value: any) {
    const { getData } = this.props;
    const customGetData = getData ? getData(value) : null;
    return customGetData ? customGetData : this.dataList.get(this.context, value);
  }

  renderOption(props: any, option: any, state: any) {
    const { getItemId } = this.props;

    const id = getItemId ? getItemId(option) : option.id;
    const name = this.getItemLabel(option);

    const matches = match(name, state.inputValue, {
      insideWords: true,
      requireMatchAll: false
    });
    const parts = parse(name, matches);

    return (
      <ListItem {...props} className='klayo-filterselector_option' key={id}>
        <ListItemText
          className='klayo-filterselector_option_text'
          primary={
            parts.length === 1 ? (
              name
            ) : (
              <div>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    className={
                      "klayo-selector_itempart" +
                      (part.highlight === true ? " klayo-selector_highlight" : "")
                    }
                  >
                    {part.text}
                  </span>
                ))}
              </div>
            )
          }
        />
      </ListItem>
    );
  }

  getItemId(item: any) {
    const { getItemId } = this.props;
    return getItemId ? getItemId(item) : item;
  }

  render() {
    const {
      label,
      sx,
      placeholder,
      focusPlaceholder,
      multiple,
      clearable,
      validationText,
      helperText,
      disabled,
      loading,
      hasNoneItem,
      noneItemLabel,
      hasAllItem,
      allItemLabel,
      noOptionsText,
      translate
    } = this.props;
    const { allValues, selectedValues, forceBlur } = this.state;

    // Use translated values if translate function is available
    const effectiveNoOptionsText =
      noOptionsText || translate?.("common.noSearchResults") || "No search results";
    const effectiveNoneItemLabel = noneItemLabel || translate?.("common.none") || "None";
    const effectiveAllItemLabel = allItemLabel || translate?.("common.all") || "All";

    const options = [];
    if (hasNoneItem) options.push({ id: -10, name: effectiveNoneItemLabel, isNoneItem: true } as T);
    if (hasAllItem) options.push({ id: -11, name: effectiveAllItemLabel, isAllItem: true } as T);

    if (allValues && allValues.length !== 0) options.push(...allValues);
    else if (this.initialItems) options.push(...this.initialItems);

    return (
      <Autocomplete
        className='klayo-filterselector klayo-selector'
        options={options}
        value={selectedValues ? selectedValues : null}
        getOptionLabel={this.getItemLabel.bind(this)}
        filterOptions={this.filterOptions}
        autoComplete
        includeInputInList
        disableClearable={!clearable}
        filterSelectedOptions
        popupIcon={<DropIcon />}
        clearIcon={<RemoveIcon />}
        onBlur={this.onBlur.bind(this)}
        handleHomeEndKeys
        noOptionsText={effectiveNoOptionsText}
        ChipProps={{
          deleteIcon: <RemoveIcon />
        }}
        multiple={multiple}
        renderOption={this.renderOption.bind(this)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            disabled={disabled}
            loading={loading}
            placeholder={placeholder || "Search"}
            focusPlaceholder={focusPlaceholder}
            forceBlur={forceBlur}
            sx={{ width: "100%" }}
            endAdornment={params.InputProps.endAdornment}
          />
        )}
        onChange={this.onChange.bind(this)}
        sx={sx}
      />
    );
  }
}
